import { Anchor, Box, Button, Container, Group, Text } from '@mantine/core';
import { IconCoin, IconCreditCard, IconLogout, IconSettings, IconUser } from '@tabler/icons-react';
import React, { createContext, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTokenManagement } from '../hooks/useTokenManagement';
import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import { AuthUser, UserRole } from '../types/auth.types';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const authState = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    authState.logout();
    window.location.href = '/login';
  };

  const { userToken, formatTokenInfo } = useTokenManagement();


  const navigateToUserManagement = () => {
    navigate('/admin/users');
  };

  const navigateToTransactionManagement = () => {
    navigate('/admin/transactions');
  };

  const navigateToTokens = () => {
    navigate('/tokens');
  };

  useEffect(() => {
    ApiService.setUnauthorizedCallback(() => {      
      // Logout và redirect
      AuthService.logout();
    });
  }, [navigate]);

  return (
    <AuthContext.Provider value={authState}>
      {authState.isLoading ? (
        <Container mt="xl" ta="center">
          <Text>Loading...</Text>
        </Container>
      ) : (
        <>
          {/* Chỉ hiển thị toàn bộ Header khi đã đăng nhập */}
          {authState.isAuthenticated && (
            <Box py="sm" px="md" style={{ borderBottom: '1px solid #e9ecef' }}>
              <Group justify="space-between" wrap="nowrap">
                {/* Logo */}
                <Anchor component={Link} to="/" style={{ textDecoration: 'none' }}>
                  <Text size="xl" fw={700} c="dark">supmmo.com</Text>
                </Anchor>

                {/* User info và các action */}
                <Group>
                  <Text fw={700} size="md">{userToken?.balance || 0} tokens</Text>
                  <Button
                    variant="filled"
                    size="sm"
                    onClick={navigateToTokens}
                    leftSection={<IconCoin size={18} />}
                    color="yellow"
                    fw={700}
                    style={{ boxShadow: '0 2px 10px rgba(255, 200, 0, 0.5)' }}
                  >
                    Mua token
                  </Button>
                  <Group gap="xs">
                    <IconUser size={16} />
                    <Text size="sm" c="dimmed">{authState.user?.email}</Text>
                  </Group>
                  {authState.user?.role === UserRole.ADMIN && (
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={navigateToUserManagement}
                      leftSection={<IconSettings size={14} />}
                    >
                      User Management
                    </Button>
                  )}
                  {authState.user?.role === UserRole.ADMIN && (
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={navigateToTransactionManagement}
                      leftSection={<IconCreditCard size={14} />}
                    >
                      Transaction Management
                    </Button>
                  )}
                  <Button 
                    variant="light" 
                    size="xs" 
                    onClick={handleLogout}
                    leftSection={<IconLogout size={14} />}
                  >
                    Logout
                  </Button>
                </Group>
              </Group>
            </Box>
          )}
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
} 